import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Button } from '../../components/ui/button';
import { Upload, X, Image, Film, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const FileUploader = ({ onUpload, accept = 'image/*,video/*', currentUrl }) => {
  const { getAuthHeader } = useAdminAuth();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl || null);
  const [isVideo, setIsVideo] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const isVideoFile = file.type.startsWith('video/');
    setIsVideo(isVideoFile);
    setPreview(URL.createObjectURL(file));

    // Upload
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/api/admin/upload`, formData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });

      const fullUrl = `${API}${response.data.url}`;
      onUpload(fullUrl);
      toast.success('Plik przesłany');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.error || 'Błąd przesyłania pliku');
      setPreview(currentUrl);
    } finally {
      setUploading(false);
    }
  };

  const clearFile = () => {
    setPreview(null);
    onUpload('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-3">
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={handleFileSelect}
        className="hidden"
      />

      {preview ? (
        <div className="relative rounded-lg overflow-hidden bg-white/5 border border-white/10">
          {isVideo ? (
            <video 
              src={preview} 
              className="w-full h-40 object-cover"
              controls={false}
            />
          ) : (
            <img 
              src={preview} 
              alt="Preview" 
              className="w-full h-40 object-cover"
            />
          )}
          <div className="absolute top-2 right-2 flex gap-2">
            <Button
              variant="destructive"
              size="icon"
              className="h-8 w-8"
              onClick={clearFile}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
          {uploading && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-white" />
            </div>
          )}
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="w-full h-40 border-2 border-dashed border-white/20 rounded-lg flex flex-col items-center justify-center gap-2 hover:border-purple-500/50 hover:bg-white/5 transition-colors"
        >
          {uploading ? (
            <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
          ) : (
            <>
              <Upload className="w-8 h-8 text-gray-500" />
              <span className="text-gray-400 text-sm">Kliknij aby przesłać plik</span>
              <span className="text-gray-600 text-xs">JPG, PNG, GIF, WebP, MP4, WebM (max 10MB)</span>
            </>
          )}
        </button>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
          className="flex-1"
        >
          <Image className="w-4 h-4 mr-2" />
          {preview ? 'Zmień plik' : 'Wybierz plik'}
        </Button>
      </div>
    </div>
  );
};

export default FileUploader;
